import { ChevronDown, Download } from "lucide-react";
import YearDropdown from "./yeardropdown";
import { useMemo, useState } from "react";
import { useStateValue } from "../../context/StateProvider";
import { formatCurrency, formatNumberShort } from "../../utils/helper";
import ColumnFilter from "./column-filter";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

function YearOver() {
  const [{ deals }] = useStateValue();

  const currentYear = new Date().getFullYear();

  const fromYearsBase = Array.from(
    { length: 5 },
    (_, i) => currentYear - (i + 1),
  );

  const toYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const [toYear, setToYear] = useState(toYears[0]);
  const [fromYear, setFromYear] = useState(fromYearsBase[0]);
  const [fromVisible, setFromVisible] = useState(false);
  const [toVisible, setToVisible] = useState(false);
  const currentMonth = new Date().getMonth();
  const [monthlypopup, setMonthlypopup] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState("All Metrics");
  const showVolume =
    selectedBreakdown === "All Metrics" || selectedBreakdown === "Volume";
  const showDeals =
    selectedBreakdown === "All Metrics" || selectedBreakdown === "Transactions";
  const showCommission =
    selectedBreakdown === "All Metrics" || selectedBreakdown === "Commission";

  const filteredFromYears = useMemo(() => {
    return fromYearsBase.filter((year) => year < toYear);
  }, [toYear]);

  const yearStats = useMemo(() => {
    const initial = {
      closedVolume: 0,
      dealsCount: 0,
      grossCommission: 0,
    };

    if (!deals?.length) return { from: initial, to: initial };

    const fromStats = { ...initial };
    const toStats = { ...initial };

    deals.forEach((deal) => {
      if (deal.dealStage !== "Closed") return;
      if (!deal.closingdate) return;

      const dealYear = new Date(deal.closingdate).getFullYear();
      const price = Number(deal.offerPrice || 0);
      const commission = Number(deal.takehomecommissionamount || 0);

      if (dealYear === fromYear) {
        fromStats.closedVolume += price;
        fromStats.dealsCount += 1;
        fromStats.grossCommission += commission;
      }

      if (dealYear === toYear) {
        toStats.closedVolume += price;
        toStats.dealsCount += 1;
        toStats.grossCommission += commission;
      }
    });

    return { from: fromStats, to: toStats };
  }, [deals, fromYear, toYear]);

  const fromAvg =
    yearStats.from.dealsCount > 0
      ? yearStats.from.closedVolume / yearStats.from.dealsCount
      : 0;

  const toAvg =
    yearStats.to.dealsCount > 0
      ? yearStats.to.closedVolume / yearStats.to.dealsCount
      : 0;

  const getChange = (fromValue, toValue) => {
    if (fromValue > 0) {
      const percent = (toValue / fromValue) * 100 - 100;
      const rounded = Math.abs(percent).toFixed(1);

      return {
        text:
          percent > 0 ? `↑ ${rounded}%` : percent < 0 ? `↓ ${rounded}%` : "0%",
        positive: percent >= 0,
      };
    }

    if (toValue > 0) {
      return { text: "↑ 100%", positive: true };
    }

    return { text: "0%", positive: true };
  };

  const volumeChange = getChange(
    yearStats.from.closedVolume,
    yearStats.to.closedVolume,
  );

  const dealsChange = getChange(
    yearStats.from.dealsCount,
    yearStats.to.dealsCount,
  );

  const commissionChange = getChange(
    yearStats.from.grossCommission,
    yearStats.to.grossCommission,
  );

  const avgChange = getChange(fromAvg, toAvg);

  const getBarHeight = (fromValue, toValue) => {
    const max = Math.max(fromValue, toValue, 1);

    const scale = 1.5;

    return {
      h1: `${(fromValue / max) * 100 * scale}%`,
      h2: `${(toValue / max) * 100 * scale}%`,
    };
  };

  const overviewCards = [
    {
      title: "TOTAL VOLUME",
      change: volumeChange.text,
      positive: volumeChange.positive,
      current: `$${formatNumberShort(yearStats.to.closedVolume)}`,
      previous: `$${formatNumberShort(yearStats.from.closedVolume)}`,
      toolTipcurrent: `${formatCurrency(yearStats.to.closedVolume)}`,
      toolTipprevious: `${formatCurrency(yearStats.from.closedVolume)}`,
      ...getBarHeight(yearStats.from.closedVolume, yearStats.to.closedVolume),
    },
    {
      title: "TRANSACTIONS",
      change: dealsChange.text,
      positive: dealsChange.positive,
      current: formatNumberShort(yearStats.to.dealsCount),
      previous: formatNumberShort(yearStats.from.dealsCount),
      ...getBarHeight(yearStats.from.dealsCount, yearStats.to.dealsCount),
    },
    {
      title: "GROSS COMMISSION",
      change: commissionChange.text,
      positive: commissionChange.positive,
      current: `$${formatNumberShort(yearStats.to.grossCommission)}`,
      previous: `$${formatNumberShort(yearStats.from.grossCommission)}`,
      toolTipcurrent: `${formatCurrency(yearStats.to.grossCommission)}`,
      toolTipprevious: `${formatCurrency(yearStats.from.grossCommission)}`,
      ...getBarHeight(
        yearStats.from.grossCommission,
        yearStats.to.grossCommission,
      ),
    },
    {
      title: "AVG SALE PRICE",
      change: avgChange.text,
      positive: avgChange.positive,
      current: `$${formatNumberShort(toAvg)}`,
      previous: `$${formatNumberShort(fromAvg)}`,
      toolTipcurrent: `${formatCurrency(toAvg)}`,
      toolTipprevious: `${formatCurrency(fromAvg)}`,
      ...getBarHeight(fromAvg, toAvg),
    },
  ];

  const monthlyStats = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => ({
      monthIndex: i,
      monthName: new Date(0, i).toLocaleString("default", { month: "long" }),

      fromVolume: 0,
      toVolume: 0,

      fromDeals: 0,
      toDeals: 0,

      fromCommission: 0,
      toCommission: 0,
    }));

    if (!deals?.length) return months;

    deals.forEach((deal) => {
      if (deal.dealStage !== "Closed") return;
      if (!deal.closingdate) return;

      const date = new Date(deal.closingdate);
      const year = date.getFullYear();
      const month = date.getMonth();

      const price = Number(deal.offerPrice || 0);
      const commission = Number(deal.takehomecommissionamount || 0);

      if (year === fromYear) {
        months[month].fromVolume += price;
        months[month].fromDeals += 1;
        months[month].fromCommission += commission;
      }

      if (year === toYear) {
        months[month].toVolume += price;
        months[month].toDeals += 1;
        months[month].toCommission += commission;
      }
    });

    return months;
  }, [deals, fromYear, toYear]);

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Monthly Breakdown");

    // 🔹 Dynamic headers
    const headers = ["Month"];

    if (showVolume) {
      headers.push(`${toYear} Volume`, `${fromYear} Volume`, "Volume Change");
    }

    if (showDeals) {
      headers.push(`${toYear} Deals`, `${fromYear} Deals`, "Deals Change");
    }

    if (showCommission) {
      headers.push(
        `${toYear} Gross Comm`,
        `${fromYear} Gross Comm`,
        "Commission Change",
      );
    }

    // Add header row
    worksheet.addRow(headers);

    // 🔹 Style header
    worksheet.getRow(1).eachCell((cell, colNumber) => {
      cell.font = { bold: true };

      cell.alignment =
        colNumber === 1 ? { horizontal: "left" } : { horizontal: "center" };
    });

    // 🔹 Add rows
    monthlyStats.forEach((m) => {
      const rowData = [m.monthName];

      if (showVolume) {
        const change = getChange(m.fromVolume, m.toVolume);
        rowData.push(m.toVolume, m.fromVolume, change.text);
      }

      if (showDeals) {
        const change = getChange(m.fromDeals, m.toDeals);
        rowData.push(m.toDeals, m.fromDeals, change.text);
      }

      if (showCommission) {
        const change = getChange(m.fromCommission, m.toCommission);
        rowData.push(m.toCommission, m.fromCommission, change.text);
      }

      const row = worksheet.addRow(rowData);

      row.eachCell((cell, colNumber) => {
        cell.alignment =
          colNumber === 1 ? { horizontal: "left" } : { horizontal: "center" };
      });
    });

    // 🔹 Auto column width
    worksheet.columns.forEach((column) => {
      column.width = 18;
    });

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Year_Comparison_${fromYear}_vs_${toYear}.xlsx`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-4 lg:pl-8 rounded-xl shadow">
        <label className="font-medium text-gray-900">Compare:</label>

        <div className="relative">
          <button
            onClick={() => setFromVisible(!fromVisible)}
            className="border bg-white rounded-md px-3 py-1.5 flex gap-x-3 items-center"
          >
            {fromYear}
            <ChevronDown size={16} />
          </button>
          <YearDropdown
            visible={fromVisible}
            options={filteredFromYears}
            selected={fromYear}
            onSelect={(year, clickedOutside) => {
              if (clickedOutside) return setFromVisible(false);

              setFromYear(year);
              if (year >= toYear) {
                setToYear(year + 1);
              }

              setFromVisible(false);
            }}
          />
        </div>

        <p className="font-medium text-base">VS</p>

        <div className="relative">
          <button
            onClick={() => setToVisible(!toVisible)}
            className="border bg-white rounded-md px-3 py-1.5 flex items-center gap-x-3"
          >
            {toYear}
            <ChevronDown size={16} />
          </button>
          <YearDropdown
            visible={toVisible}
            options={toYears}
            selected={toYear}
            onSelect={(year, clickedOutside) => {
              if (clickedOutside) return setToVisible(false);

              setToYear(year);
              if (fromYear >= year) {
                setFromYear(year - 1);
              }

              setToVisible(false);
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-8">
        {overviewCards.map((item, i) => (
          <div
            key={i}
            className="bg-white p-4 rounded-2xl shadow flex flex-col"
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-500">{item.title}</span>

              <span
                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  item.positive
                    ? "bg-green-100 text-green-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {item.change}
              </span>
            </div>

            <div className="mt-3">
              <div className="flex items-end gap-2 h-[110px]">
                <div className="flex flex-col items-center w-1/2 justify-end h-full">
                  {/* title={item.toolTipprevious} */}
                  <div className="group">
                    <div className="text-xl font-semibold text-gray-900">
                      {item.previous}
                    </div>
                    {item.toolTipprevious && (
                      <div className="hidden group-hover:block absolute bg-white p-1 rounded-lg border border-gray-200 shadow-sm text-xs">
                        {item.toolTipprevious}
                      </div>
                    )}
                  </div>
                  <div
                    className="bg-amber-400 rounded-t w-full transition-all duration-500"
                    style={{ height: item.h1 }}
                  ></div>
                  <span className="text-xs text-gray-500 mt-1">{fromYear}</span>
                </div>

                <div className="flex flex-col items-center w-1/2 justify-end h-full">
                  <div className="group">
                    <div className="text-xl font-semibold text-gray-900">
                      {item.current}
                    </div>
                    {item.toolTipcurrent && (
                      <div className="hidden group-hover:block absolute bg-white p-1 rounded-lg border border-gray-200 shadow-sm text-xs">
                        {item.toolTipcurrent}
                      </div>
                    )}
                  </div>
                  <div
                    className="bg-[#1E293B] rounded-t w-full transition-all duration-500"
                    style={{ height: item.h2 }}
                  ></div>
                  <span className="text-xs text-gray-500 mt-1">{toYear}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow lg:p-6 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 p-5 lg:p-0">
          <h2 className="text-xl font-semibold">Monthly Breakdown</h2>
          <div className="flex gap-x-3">
            <div className="relative">
              <div
                onClick={() => setMonthlypopup(!monthlypopup)}
                className={`w-full border-2 border-gray-300 ${
                  selectedBreakdown === "" ? "text-gray-400" : "text-textcolor"
                } rounded-lg p-2 cursor-pointer`}
              >
                {selectedBreakdown}
              </div>

              <ColumnFilter
                style={{ top: "50px" }}
                visible={monthlypopup}
                activateoutsidecontainerclick={() => setMonthlypopup(false)}
                onClickcorrect={(value) => {
                  setSelectedBreakdown(value);
                  setMonthlypopup(false);
                }}
              />
            </div>
            <button
              onClick={() => exportToExcel(fromYear, toYear)}
              className="relative inline-flex items-center gap-2 px-6 py-2 font-semibold text-gray-800 text-sm rounded-lg bg-gradient-to-r from-white/90 to-gray-200 shadow-md hover:-translate-y-1 hover:shadow-lg transition duration-300 overflow-hidden group h-[40px]"
            >
              {/* Shine Animation */}
              <span className="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-150 transition-transform duration-700 ease-out"></span>
              <Download />
              <span className="relative">Export</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-100 panelScroll">
          <table className="min-w-[700px] w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 text-gray-500 uppercase text-xs font-semibold">
                  Month
                </th>

                {showVolume && (
                  <>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      {toYear} Volume
                    </th>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      {fromYear} Volume
                    </th>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      Change
                    </th>
                  </>
                )}

                {showDeals && (
                  <>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      {toYear} Deals
                    </th>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      {fromYear} Deals
                    </th>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      Change
                    </th>
                  </>
                )}

                {showCommission && (
                  <>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      {toYear} Gross Comm
                    </th>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      {fromYear} Gross Comm
                    </th>
                    <th className="p-3 text-center text-gray-500 uppercase text-xs font-semibold">
                      Change
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {monthlyStats.map((m, i) => {
                const volumeChange = getChange(m.fromVolume, m.toVolume);
                const dealsChange = getChange(m.fromDeals, m.toDeals);
                const commissionChange = getChange(
                  m.fromCommission,
                  m.toCommission,
                );
                const curr = i === currentMonth;

                return (
                  <tr
                    key={i}
                    className="border-b border-gray-200 hover:bg-gray-50 transition"
                  >
                    <td className="p-3">
                      {m.monthName}
                      {curr && (
                        <span className="text-[10px] font-semibold bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full ml-2">
                          NOW
                        </span>
                      )}
                    </td>

                    {showVolume && (
                      <>
                        <td className="p-3 text-center">
                          {formatCurrency(m.toVolume)}
                        </td>
                        <td className="p-3 text-center">
                          {formatCurrency(m.fromVolume)}
                        </td>
                        <td
                          className={`p-3 text-center font-medium ${volumeChange.positive ? "text-green-600" : "text-red-600"}`}
                        >
                          {volumeChange.text}
                        </td>
                      </>
                    )}

                    {showDeals && (
                      <>
                        <td className="p-3 text-center">{m.toDeals}</td>
                        <td className="p-3 text-center">{m.fromDeals}</td>
                        <td
                          className={`p-3 text-center font-medium ${dealsChange.positive ? "text-green-600" : "text-red-600"}`}
                        >
                          {dealsChange.text}
                        </td>
                      </>
                    )}

                    {showCommission && (
                      <>
                        <td className="p-3 text-center">
                          {formatCurrency(m.toCommission)}
                        </td>
                        <td className="p-3 text-center">
                          {formatCurrency(m.fromCommission)}
                        </td>
                        <td
                          className={`p-3 text-center font-medium ${commissionChange.positive ? "text-green-600" : "text-red-600"}`}
                        >
                          {commissionChange.text}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default YearOver;
